import { checkProductStock } from '../../../service/productService';
import ProductSetItemModel from '../models/product_set_item';
import VariationStockInfoModel from '../models/variation_stock_info';
import { getCartInitData } from '../../../utils/cart';
import { getProductImageUrl } from '../utils';
import './index.scss';

const ONE_PAGE_STORE_ID = getCartInitData()?.extraMainConfig?.onePageStoreId;

export class ProductSetItemPicker extends HTMLElement {
  constructor() {
    super();
    this.model = {
      product: null,
      selected: {},
      selectedVariantKey: null,
      quantitySelectValue: 1,
      isInitialization: false,
      loading: false,
      error: '',
      disabled: true,
    };
    this.variationStockInfoModel = null;
    this.productModel = null;
    this.onEventsBind = this.onEvents.bind(this);
  }
  get selectedVariant() {
    const { selectedVariantKey } = this.model;
    const { hasVariations, variantsMap } = this.productModel;

    return hasVariations ? variantsMap[selectedVariantKey] : null;
  }
  get finishSelected() {
    const { selected } = this.model;
    const { mustSelectNum } = this.productModel;

    return selected?.selectedNum === mustSelectNum;
  }
  get availableQuantitySelectValue() {
    const { selected, quantitySelectValue } = this.model;
    const { selectedNum = 0 } = selected;
    const { mustSelectNum } = this.productModel;
    const count = selectedNum + quantitySelectValue;
    return count <= mustSelectNum;
  }
  get variationQuantityOfStockHintText() {
    const { variationStockInfoModel } = this;
    const { selected, selectedVariantKey } = this.model;

    if (!variationStockInfoModel || !selectedVariantKey) {
      return '';
    }

    return variationStockInfoModel.computedQuantityOfStockHintText(
      selected.selectedVariationsNum?.[selectedVariantKey] || 0,
    );
  }
  getNgServices() {
    const injector = window.angular?.element('body')?.injector?.();
    return injector
      ? {
          trackerService: injector.get('trackerService'),
          cartService: injector.get('cartService'),
        }
      : null;
  }
  connectedCallback() {
    this.bindEvents();
  }
  disconnectedCallback() {
    this.removeEventsListener();
  }
  bindEvents() {
    this.addEventListener('click', this.onEventsBind);
  }
  removeEventsListener() {
    this.removeEventListener('click', this.onEventsBind);
  }
  setSelectedVariantKey(key) {
    const { loading, disabled } = this.model;
    if (
      disabled ||
      this.model.selectedVariantKey === key ||
      loading ||
      this.finishSelected
    ) {
      return;
    }
    this.model.selectedVariantKey = key;
    this.variationStockInfoModel = null;
    this.model.quantitySelectValue = 1;
    this.querySelector('quantity-select')?.setValue?.(1);
    this.setError('');
    this.fetchStockInfo(key).then(() => {
      if (key !== this.model.selectedVariantKey) {
        return;
      }
      this.updateErrorByComputed();
      this.render();
    });
    this.sendVariationSelectedTrackEvent();
    this.render();
  }
  sendVariationSelectedTrackEvent() {
    const ngServices = this.getNgServices();
    const { selectedVariantKey, disabled } = this.model;
    if (disabled || !ngServices) {
      return;
    }
    const { product } = this.productModel;
    const { trackerService, cartService } = ngServices;
    const selectedVariation = this.productModel.variantsMap[selectedVariantKey];
    trackerService.gaSendDetails(product, selectedVariation, null);
    const price = cartService.getItemPrice({
      product: product,
      variation: selectedVariation,
    });
    const dollars = price.dollars || 0;
    trackerService.fbSendViewContent(product, dollars, selectedVariation);
    trackerService.track({
      type: trackerService.generalEventType.VIEW_CONTENT,
      data: {
        product: product,
        value: dollars,
        variationSelected: selectedVariation,
      },
    });
  }
  onEvents(e) {
    const { disabled } = this.model;

    if (disabled) {
      return;
    }
    if (e.target.closest('.js-add-button')) {
      this.onCheckStock();
      return;
    }
    if (e.target.closest('.js-option-select')) {
      this.onOptionSelect(e.target.closest('.js-option-select'));
      return;
    }
    if (e.target.closest('.js-variant-remove')) {
      this.onVariantRemove(e.target.closest('.js-variant-remove'));
      return;
    }
  }
  updateErrorByComputed() {
    const { variationStockInfoModel } = this;
    const { quantitySelectValue, disabled } = this.model;
    if (!variationStockInfoModel) {
      this.setError('');
      return;
    }
    if (!this.availableQuantitySelectValue) {
      this.setError(
        window.shopline.i18next.t(
          'Exceed product set purchase limit, please adjust your quantity',
          {
            ns: 'translation',
          },
        ),
      );
      return;
    }
    if (!this.checkStock(quantitySelectValue)) {
      this.setError(
        window.shopline.i18next.t('variant out of stock', {
          ns: 'translation',
        }),
      );
      return;
    }
    const variationQuantityOfStockHintText =
      this.variationQuantityOfStockHintText;
    if (!this.finishSelected && !disabled && variationQuantityOfStockHintText) {
      this.setError(variationQuantityOfStockHintText);
      return;
    }
    this.setError('');
  }
  fetchStockInfo(variationKey) {
    return checkProductStock(
      this.productModel.product._id,
      variationKey,
      ONE_PAGE_STORE_ID,
    ).then((response) => {
      if (this.model.selectedVariantKey === variationKey && response?.data) {
        this.variationStockInfoModel = new VariationStockInfoModel(
          response.data,
        );
      }
    });
  }
  onCheckStock() {
    const { selectedVariantKey, loading, quantitySelectValue } = this.model;
    const variant = this.productModel.variantsMap[selectedVariantKey];

    if (!this.availableQuantitySelectValue && !this.finishSelected) {
      this.updateErrorByComputed();
      this.render();
      return;
    }

    if (
      loading ||
      this.finishSelected ||
      !variant ||
      !this.availableQuantitySelectValue
    ) {
      return;
    }
    this.setError('');
    this.model.loading = true;
    this.renderActionButton();
    this.fetchStockInfo(selectedVariantKey)
      .then(() => {
        if (this.checkStock(quantitySelectValue)) {
          this.onVariantAdd(selectedVariantKey, quantitySelectValue);
          if (!this.finishSelected && this.variationQuantityOfStockHintText) {
            this.setError(this.variationQuantityOfStockHintText);
          }
        } else {
          this.setError(
            window.shopline.i18next.t(
              'out of stock can not add to product-set',
              {
                ns: 'translation',
              },
            ),
          );
          const { _id } = this.productModel.product;
          const { quantity, preorder_limit } =
            this.variationStockInfoModel.data;
          this.closest('product-set-picker').updateChildProductData(
            _id,
            selectedVariantKey,
            {
              quantity,
              preorder_limit,
            },
          );
        }
      })
      .finally(() => {
        this.model.loading = false;
        this.render();
      });
  }
  checkStock(value, variantKey) {
    const { selected, selectedVariantKey } = this.model;
    const needValue =
      (value || 1) +
      (selected.selectedVariationsNum[variantKey || selectedVariantKey] || 0);
    return this.variationStockInfoModel.checkStock(needValue);
  }
  onVariantAdd(variantKey, num) {
    const { selected } = this.model;
    const prevNum = selected?.selectedVariationsNum?.[variantKey] || 0;
    const nextNum = prevNum + num;
    this.closest('product-set-picker').onSelectedChange(
      this.productModel.product._id,
      variantKey,
      nextNum,
    );
  }
  onOptionSelect($optionSelect) {
    if (
      $optionSelect.classList.contains('selected') ||
      $optionSelect.classList.contains('disabled') ||
      !this.selectedVariant
    ) {
      return;
    }
    this.setError('');
    const fieldIndex = Number($optionSelect.getAttribute('data-field-index'));
    const optionKey = $optionSelect.getAttribute('data-option-key');
    const newOptionKeys = this.selectedVariant.variant_option_ids.map(
      (key, index) => {
        return index === fieldIndex ? optionKey : key;
      },
    );
    const nextSelectVariant =
      this.productModel.fuzzyMathVariationByOptionKeys(newOptionKeys);
    if (nextSelectVariant) {
      this.setSelectedVariantKey(nextSelectVariant.key);
    }
  }
  onVariantRemove($variant) {
    if (this.model.disabled) {
      return;
    }
    const variantKey = $variant.getAttribute('data-variant-key');
    this.closest('product-set-picker').onSelectedChange(
      this.productModel.product._id,
      variantKey,
      0,
    );
  }
  computedMaxSelectByVariationKey(key) {
    const { selected } = this.model;
    const { variationsQuantityInfo } = this.productModel;
    const max = variationsQuantityInfo?.[key]?.max || 0;
    const selectedNum = selected?.selectedNum || 0;
    const maxSelect = max - selectedNum;

    return maxSelect > 0 ? maxSelect : 0;
  }
  updateModel(model) {
    this.productModel = new ProductSetItemModel(model.product);
    this.model = Object.assign(this.model, model);
    this.render();
  }
  getFieldsTemplate() {
    const { hasVariations } = this.productModel;
    const errorTemplate =
      '<div class="product-set-item-picker__hint js-error"></div>';

    if (!hasVariations) {
      return errorTemplate;
    }

    const template = `
      <div class="js-fields"></div>
      <div class="product-set-item-picker__actions">
        <quantity-select></quantity-select>
        <button class="product-set-item-picker__add-button js-add-button">
          <span class="action-name"></span>
          <span class="fa fa-spinner loading-icon"></span>
        </button>
      </div>
      ${errorTemplate}
    `;

    return template;
  }
  initialization() {
    if (this.model.isInitialization) {
      return;
    }
    this.model.isInitialization = true;
    this.initRender();
    this.setQuantitySelectValueChange();
    const firstCanSelectVariation = this.productModel.firstCanSelectVariation;
    if (firstCanSelectVariation) {
      this.setSelectedVariantKey(firstCanSelectVariation?.key);
    }
  }
  setQuantitySelectValueChange() {
    window.requestAnimationFrame(() => {
      const $quantitySelect = this.querySelector('quantity-select');
      if ($quantitySelect) {
        $quantitySelect.onValueChange = (value) => {
          this.model.quantitySelectValue = value;
          this.updateErrorByComputed();
          this.render();
        };
      }
    });
  }
  render() {
    if (!this.model.isInitialization) {
      this.initialization();
    }
    this.renderSelectTip();
    this.renderError();
    if (this.productModel.hasVariations) {
      this.renderImage();
      this.renderOptions();
      this.renderQuantitySelect();
      this.renderActionButton();
      this.renderQuantitySelected();
    }
  }
  initRender() {
    const { product } = this.model;
    const { title_translations } = product;
    const template = `
      <div class="product-set-item-picker__select-tip js-select-tip"></div>
      <div class="product-set-picker__name-info for-item">
        <img src="${getProductImageUrl(product, this.model.selectedVariantKey, { size: '160x' })}" loading="lazy">
        <div class="product-set-picker__name-info-title">
          <span>${window.shopline.translateModel(title_translations)}</span>
        </div>
      </div>
      ${this.getFieldsTemplate()}
    `;
    this.innerHTML = template;
  }
  renderSelectTip() {
    const $tip = this.querySelector('.js-select-tip');
    const { mustSelectNum } = this.productModel;
    const { selected, disabled } = this.model;
    const { selectedNum = 0 } = selected;

    let tip = window.shopline.i18next.t('n items must selected', {
      ns: 'translation',
      n: mustSelectNum,
    });

    if (this.finishSelected) {
      tip = window.shopline.i18next.t('n items selected', {
        ns: 'translation',
        n: mustSelectNum,
      });
    } else if (!this.productModel.hasSufficientStock) {
      tip = window.shopline.i18next.t('n items must selected by sold out', {
        ns: 'translation',
        n: mustSelectNum,
      });
    } else if (selectedNum > 0) {
      tip = window.shopline.i18next.t('n/total items selected', {
        ns: 'translation',
        n: selectedNum,
        total: mustSelectNum,
      });
      tip = window.shopline.i18next.t('n items needed', {
        ns: 'translation',
        n: mustSelectNum - selectedNum,
      });
    }
    if (disabled || this.finishSelected) {
      $tip.classList.remove('primary');
    } else {
      $tip.classList.add('primary');
    }
    $tip.innerHTML = tip;
  }
  renderImage() {
    const { product } = this.productModel;
    const { selectedVariantKey } = this.model;
    this.querySelector('img').setAttribute(
      'src',
      getProductImageUrl(product, selectedVariantKey, { size: '160x' }),
    );
  }
  renderOptions() {
    const { disabled } = this.model;
    const fields = this.productModel.computedVariantsFieldsByVariant(
      this.selectedVariant,
    );

    if (fields.length === 0) {
      return;
    }

    const template = `
      ${fields
        .map(
          (field, fieldIndex) => `
            <div class="product-set-item-picker__options">
              <span class="name">${window.shopline.translateModel(
                field.name_translations,
              )}</span>
              ${field.variantOptions
                .map((option) => {
                  const classList = ['option', 'js-option-select'];
                  const optionKey = option.key;
                  const optionName = window.shopline.translateModel(
                    option.name_translations,
                  );
                  const hasStock = this.productModel.checkHasStockByOptionKey(
                    optionKey,
                    fieldIndex,
                  );
                  const isSelected = this.productModel.matchVariationOptionKey(
                    this.selectedVariant,
                    optionKey,
                    fieldIndex,
                  );
                  if (
                    disabled ||
                    this.finishSelected ||
                    (!hasStock && !isSelected)
                  ) {
                    classList.push('disabled');
                  } else if (isSelected) {
                    classList.push('selected');
                  }
                  return `
                    <span
                      class="${classList.join(' ')}"
                      data-option-key=${option.key}
                      data-field-index="${fieldIndex}">
                        ${optionName}
                    </span>`;
                })
                .join('')}
            </div>
        `,
        )
        .join('')}
    `;

    this.querySelector('.js-fields').innerHTML = template;
  }
  renderSelectVariant() {
    const { selectedVariantKey } = this.model;
    const $prevSelectVariant = this.querySelector(
      `.js-variant-select.selected`,
    );
    if (
      $prevSelectVariant &&
      $prevSelectVariant.getAttribute('data-variation-key') ===
        selectedVariantKey
    ) {
      return;
    }
    $prevSelectVariant.classList.remove('selected');

    if (selectedVariantKey) {
      this.querySelector(
        '.js-variant-select["${data-variation-key}=${selectedVariantKey}"]',
      )?.classList?.add('selected');
    }
  }
  renderQuantitySelect() {
    if (!this.productModel.hasVariations) {
      return;
    }
    const { disabled } = this.model;
    const $quantitySelect = this.querySelector('quantity-select');

    if (disabled || this.finishSelected) {
      $quantitySelect.setAttribute('disabled', 'disabled');
    } else if (this.model.selectedVariantKey) {
      $quantitySelect.removeAttribute('disabled');
    } else {
      $quantitySelect.setAttribute('disabled', 'disabled');
    }
  }
  initRenderQuantitySelected() {
    const $list = document.createElement('div');
    $list.classList.add('product-set-item-picker__selected-variants');
    $list.classList.add('js-variants-selected');

    this.appendChild($list);
  }
  renderQuantitySelected() {
    if (!this.querySelector('.js-variants-selected')) {
      this.initRenderQuantitySelected();
    }
    const { selected } = this.model;
    const { selectedVariationsNum = {} } = selected;
    const $list = this.querySelector('.js-variants-selected');

    $list.innerHTML = Object.keys(selectedVariationsNum)
      .map((variantKey) => {
        const value = selectedVariationsNum[variantKey];
        const variant = this.productModel.variantsMap[variantKey];
        const { fields = [] } = variant;

        return `
          <div class="product-set-item-picker__selected-variant">
            <span class="name">${fields
              .map((field) =>
                window.shopline.translateModel(field.name_translations),
              )
              .join('/')}</span>
            <span class="num">x${value}</span>
            <span class="fa fa-trash-o js-variant-remove" data-variant-key="${variantKey}"></span>
          </div>
        `;
      })
      .join('');
  }
  renderActionButton() {
    const { hasVariations, hasSufficientStock } = this.productModel;
    const { loading, disabled } = this.model;
    if (!hasVariations) {
      return;
    }
    let buttonText = window.shopline.i18next.t('Add to product set', {
      ns: 'translation',
    });
    if (this.finishSelected) {
      buttonText = window.shopline.i18next.t('Limit reached', {
        ns: 'translation',
      });
    } else if (!hasSufficientStock) {
      buttonText = window.shopline.i18next.t('Sold out', {
        ns: 'translation',
      });
    }
    const $button = this.querySelector('.js-add-button');
    const $actionName = $button.querySelector('.action-name');
    $actionName.innerHTML = buttonText;
    if (loading) {
      $button.classList.add('loading');
    } else {
      $button.classList.remove('loading');
    }
    if (disabled || this.finishSelected) {
      $button.classList.add('disabled');
    } else {
      $button.classList.remove('disabled');
    }
  }
  setError(error) {
    this.model.error = error;
  }
  renderError() {
    const { error } = this.model;
    let template = '';
    if (error) {
      template = `<span>${error}</span>`;
    }
    this.querySelector('.js-error').innerHTML = template;
  }
}

if (!customElements.get('product-set-item-picker')) {
  customElements.define('product-set-item-picker', ProductSetItemPicker);
}
